import * as React from 'react';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { cn } from '../../lib/utils';

/**
 * DropdownMenu — shadcn API on the Base UI Menu. Base UI supplies
 * `role="menu"` / `menuitem`, arrow keys, Home / End, typeahead, Escape and
 * focus return; `DropdownMenuContent` wraps Portal + Positioner + Popup like
 * `PopoverContent` does. Use it for row actions and account menus instead of a
 * Popover of buttons.
 */
const DropdownMenu = BaseMenu.Root;
const DropdownMenuTrigger = BaseMenu.Trigger;
const DropdownMenuGroup = BaseMenu.Group;

type PositionerProps = React.ComponentPropsWithoutRef<typeof BaseMenu.Positioner>;
type PopupProps = React.ComponentPropsWithoutRef<typeof BaseMenu.Popup>;

export interface DropdownMenuContentProps extends PopupProps {
  align?: PositionerProps['align'];
  side?: PositionerProps['side'];
  sideOffset?: PositionerProps['sideOffset'];
  alignOffset?: PositionerProps['alignOffset'];
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, align = 'end', side, sideOffset = 4, alignOffset, ...props }, ref) => (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className="z-50"
      >
        <BaseMenu.Popup
          ref={ref}
          className={cn(
            'min-w-40 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none',
            'data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0',
            className,
          )}
          {...props}
        />
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  ),
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof BaseMenu.Item
> {
  /** `destructive` colours the item with the danger token. */
  variant?: 'default' | 'destructive';
}

const DropdownMenuItem = React.forwardRef<HTMLElement, DropdownMenuItemProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <BaseMenu.Item
      ref={ref}
      data-variant={variant}
      className={cn(
        'flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        variant === 'destructive' && 'text-[var(--color-danger)]',
        className,
      )}
      {...props}
    />
  ),
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

/** Group heading; place it inside a `DropdownMenuGroup`. */
const DropdownMenuLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.GroupLabel>
>(({ className, ...props }, ref) => (
  <BaseMenu.GroupLabel
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-medium text-[var(--color-foreground-3)]', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.Separator>
>(({ className, ...props }, ref) => (
  <BaseMenu.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[var(--color-hairline)]', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
